'use client';

import { useTranslations } from 'next-intl';
import type { BundleOption, BundleOptionSelection } from '@cobrastyle/shared-types';
import { Text } from '@cobrastyle/ui';
// See lib/format-price: one formatter, so a bundle child agrees with its own
// product page.
import { formatPrice } from '@/lib/format-price';
import type { BundleSelectionState } from '@/lib/bundle';
import { QuantitySelector } from './quantity-selector';

interface BundleOptionsProps {
  options: BundleOption[];
  state: BundleSelectionState;
  onChange: (optionId: number, selections: Array<{ uid: string; quantity: number }>) => void;
  currency: string;
}

export function BundleOptions({ options, state, onChange, currency }: BundleOptionsProps) {
  const t = useTranslations('product');

  const chosen = (optionId: number) => state[optionId] ?? [];
  const isChosen = (optionId: number, uid: string) => chosen(optionId).some((c) => c.uid === uid);
  const qtyOf = (optionId: number, uid: string) =>
    chosen(optionId).find((c) => c.uid === uid)?.quantity;

  const selectSingle = (option: BundleOption, sel: BundleOptionSelection) =>
    onChange(option.optionId, [{ uid: sel.uid, quantity: sel.quantity }]);

  const toggleMulti = (option: BundleOption, sel: BundleOptionSelection) => {
    const current = chosen(option.optionId);
    const next = current.some((c) => c.uid === sel.uid)
      ? current.filter((c) => c.uid !== sel.uid)
      : [...current, { uid: sel.uid, quantity: sel.quantity }];
    onChange(option.optionId, next);
  };

  const changeQty = (option: BundleOption, uid: string, quantity: number) =>
    onChange(
      option.optionId,
      chosen(option.optionId).map((c) => (c.uid === uid ? { ...c, quantity } : c)),
    );

  const label = (sel: BundleOptionSelection) => (
    <span className="flex min-w-0 flex-1 items-center gap-2">
      <span className="truncate">{sel.label}</span>
      {sel.productPrice != null && sel.productPrice > 0 && (
        <span className="shrink-0 text-sm font-semibold text-price">
          {formatPrice(sel.productPrice, currency)}
        </span>
      )}
    </span>
  );

  return (
    <div className="space-y-8">
      {options.map((option) => {
        const groupId = `bundle-opt-${option.optionId}`;
        return (
          <div key={option.optionId}>
            <Text as="h2" size="sm" weight="medium" variant="muted" className="mb-4 uppercase tracking-wide text-muted">
              {option.title}
              {option.required && <span className="ml-1 text-primary">*</span>}
            </Text>

            {option.type === 'select' ? (
              <>
                <select
                  aria-label={option.title}
                  value={chosen(option.optionId)[0]?.uid ?? ''}
                  onChange={(e) => {
                    const sel = option.selections.find((s) => s.uid === e.target.value);
                    if (sel) selectSingle(option, sel);
                  }}
                  className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none"
                >
                  <option value="" disabled>
                    {t('selectOption', { option: option.title })}
                  </option>
                  {option.selections.map((sel) => (
                    <option key={sel.uid} value={sel.uid}>
                      {sel.label}
                      {sel.productPrice ? ` (+${sel.productPrice} ${currency})` : ''}
                    </option>
                  ))}
                </select>
                {(() => {
                  const selectedUid = chosen(option.optionId)[0]?.uid;
                  const selectedSel = option.selections.find((s) => s.uid === selectedUid);
                  if (!selectedSel || !selectedSel.canChangeQuantity) return null;
                  return (
                    <div className="mt-2">
                      <QuantitySelector
                        value={qtyOf(option.optionId, selectedSel.uid) ?? selectedSel.quantity}
                        onChange={(q) => changeQty(option, selectedSel.uid, q)}
                        min={1}
                      />
                    </div>
                  );
                })()}
              </>
            ) : (
              <div className="space-y-2">
                {option.selections.map((sel) => {
                  const selected = isChosen(option.optionId, sel.uid);
                  const multi = option.type === 'checkbox' || option.type === 'multi';
                  return (
                    <label
                      key={sel.uid}
                      className="flex flex-wrap items-center gap-3 rounded-lg border border-border p-3 text-sm text-foreground hover:border-primary"
                    >
                      <input
                        type={multi ? 'checkbox' : 'radio'}
                        name={groupId}
                        checked={selected}
                        onChange={() => (multi ? toggleMulti(option, sel) : selectSingle(option, sel))}
                        className="accent-primary"
                      />
                      {label(sel)}
                      {selected && sel.canChangeQuantity && (
                        <span className="ml-auto">
                          <QuantitySelector
                            value={qtyOf(option.optionId, sel.uid) ?? sel.quantity}
                            onChange={(q) => changeQty(option, sel.uid, q)}
                            min={1}
                          />
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
